import { useState } from 'react';
import { supabase } from '../supabaseClient';
import SearchInput from './SearchInput';
import '../styles/DashboardNewSearch.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const PLACEHOLDER_EXAMPLES = [
  'Recent college grad building in agentic AI, pre-seed',
  'First-time founder with 5 years robotics experience, raising pre-seed',
  'Ex-Stripe engineer, 3 years payments experience, building fintech',
  'Healthcare founder, former nurse, bootstrapping telehealth startup',
  'AI researcher, PhD dropout, building developer tools',
  'First-time founder with 7 years supply chain experience at Amazon',
  'Climate tech founder, 4 years energy sector experience',
  'Former product manager, building vertical SaaS, pre-revenue',
  'Logistics founder with 6 years warehouse automation experience',
  'First-time founder, ex-Google, building consumer social app',
  'Biotech founder, 8 years pharma research background',
  'EdTech founder, former teacher, just launched MVP',
  'Web3 founder with 3 years crypto engineering experience',
  'Hardware founder, 5 years manufacturing experience, seeking pre-seed',
  'Marketplace founder, college dropout, early traction'
];

function DashboardNewSearch({ user, refreshSearches, setActiveSection, templates = [] }) {
  const [query, setQuery] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [popupType, setPopupType] = useState('error'); // 'error', 'success', or 'blocked'
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Query expansion state
  const [isExpanded, setIsExpanded] = useState(false);
  const [isExpanding, setIsExpanding] = useState(false);
  const [isHighlighting, setIsHighlighting] = useState(false);
  const [formattedQuery, setFormattedQuery] = useState(null);

  // Template state
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [expandedTemplateText, setExpandedTemplateText] = useState(null);

  // Handle template selection
  const handleTemplateClick = (template) => {
    if (isExpanded) return;

    if (selectedTemplate?.id === template.id) {
      setSelectedTemplate(null);
    } else {
      setSelectedTemplate(template);
    }
  };

  // Get the full query for search execution (combines expanded query + template)
  const getFullQueryForSearch = () => {
    const baseQuery = query.trim();
    if (expandedTemplateText) {
      return `${baseQuery} ${expandedTemplateText}`;
    }
    return baseQuery;
  };

  // Check if we have any content to submit
  const hasContent = () => {
    return query.trim() || selectedTemplate;
  };

  // Handle button click - either expand query or start search
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!hasContent()) {
      return;
    }

    // If query is already expanded, start the search
    if (isExpanded) {
      executeSearch(getFullQueryForSearch());
      return;
    }

    // Otherwise, expand the query first
    const startTime = Date.now();
    const minDuration = 2500; // 2.5 seconds minimum
    setIsExpanding(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const requestBody = { query: query.trim() };
      if (selectedTemplate) {
        requestBody.template = selectedTemplate.text;
      }

      const response = await fetch(`${BACKEND_URL}/query/expand-query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.detail || 'Failed to expand query');
      }

      const parsed = await response.json();

      // Wait for minimum duration before applying changes
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minDuration - elapsedTime);

      await new Promise(resolve => setTimeout(resolve, remainingTime));

      // Now apply all the state updates together
      setQuery(parsed.expanded_unformatted_query);
      setFormattedQuery(parsed.expanded_formatted_query);
      if (parsed.template) {
        setExpandedTemplateText(parsed.template);
      }
      setSelectedTemplate(null);
      setIsExpanded(true);
      setIsHighlighting(true);
      setIsExpanding(false);
      setTimeout(() => setIsHighlighting(false), 3500);
    } catch (err) {
      console.error('Error expanding query:', err);
      setPopupType('error');
      setPopupMessage(err.message || 'Failed to expand your query. Please try again.');
      setShowPopup(true);
      setIsExpanding(false);
    }
  };

  // Actually execute the search
  const executeSearch = async (searchQuery) => {
    setIsSubmitting(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${BACKEND_URL}/leads/create-find-leads-job`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          query: searchQuery
        })
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.detail || 'Failed to create search');
      }

      // Success - reset form and show success popup
      setQuery('');
      setIsExpanded(false);
      setSelectedTemplate(null);
      setExpandedTemplateText(null);
      setPopupType('success');
      setPopupMessage('This will take 4-7 minutes, so you can sit back or grab a coffee.');
      setShowPopup(true);

      if (refreshSearches) {
        refreshSearches();
      }
    } catch (err) {
      console.error('Error submitting search:', err);
      setPopupType('error');
      setPopupMessage(err.message || 'An error occurred while creating your search. Please try again.');
      setShowPopup(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const closePopup = () => {
    setShowPopup(false);
    setPopupMessage('');
  };

  const handleSuccessClose = () => {
    setShowPopup(false);
    setPopupMessage('');
    if (setActiveSection) {
      setActiveSection('searches');
    }
  };

  return (
    <div className="dashboard-new-search">
      <h1 className="new-search-title">Who should I look for next?</h1>

      <form onSubmit={handleSubmit} className="new-search-form">
        <SearchInput
          query={query}
          onQueryChange={setQuery}
          onSubmit={handleSubmit}
          isExpanded={isExpanded}
          isExpanding={isExpanding}
          isSubmitting={isSubmitting}
          isHighlighting={isHighlighting}
          formattedQuery={formattedQuery}
          onHighlightingStop={() => setIsHighlighting(false)}
          placeholderExamples={PLACEHOLDER_EXAMPLES}
          expandedButtonText="Start search"
          selectedTemplate={selectedTemplate}
          expandedTemplateText={expandedTemplateText}
          templates={templates}
          onTemplateClick={handleTemplateClick}
          onCreateTemplate={() => setActiveSection('templates')}
        />
      </form>

      {/* Success popup */}
      {showPopup && popupType === 'success' && (
        <div className="new-search-popup-overlay">
          <div className="new-search-popup success" onClick={(e) => e.stopPropagation()}>
            <h3 className="new-search-popup-title">Your search is brewing!</h3>
            <p className="new-search-popup-message">{popupMessage}</p>
            <button onClick={handleSuccessClose} className="new-search-popup-button">
              View Progress
            </button>
          </div>
        </div>
      )}

      {/* Error popup */}
      {showPopup && popupType === 'error' && (
        <div className="new-search-popup-overlay" onClick={closePopup}>
          <div className="new-search-popup error" onClick={(e) => e.stopPropagation()}>
            <p className="new-search-popup-message">{popupMessage}</p>
            <button onClick={closePopup} className="new-search-popup-button">
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardNewSearch;
